import { useCallback, useMemo, useRef } from 'react';
import { MODULE_NAMES } from '../../../Constants/modules';

const buildFilterValue = (value = '', label = value) => {
  const normalizedValue = `${value || ''}`.trim();
  return normalizedValue ? { label: label || normalizedValue, value: normalizedValue } : null;
};

const buildNotificationFilter = (field, value) => (value ? {
  field,
  selectedValues: [value],
  type: 'filter',
} : null);

export const useBillingNotifFilters = () => {
  const initialFilters = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const billingReportId = buildFilterValue(params.get('billingReportId'));
    const status = buildFilterValue(params.get('requestStatus'));
    const projectId = buildFilterValue(
      params.get('projectId'),
      params.get('projectName') || params.get('projectId'),
    );
    return [
      buildNotificationFilter('billingReportId', billingReportId),
      buildNotificationFilter('status', status),
      buildNotificationFilter('projectName', projectId),
    ].filter(Boolean);
  }, []);
  const activeFilters = useRef(initialFilters);
  const filtersActive = useRef(Boolean(initialFilters.length));

  const handleFilterUpdate = useCallback(() => {
    filtersActive.current = false;
    activeFilters.current = [];
    const url = new URL(window.location.href);
    ['billingReportId', 'projectId', 'projectName', 'requestStatus', 'fromNotification']
      .forEach((key) => url.searchParams.delete(key));
    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
  }, []);

  const handleApprovalUpdated = useCallback(() => {
    const projectFilter = initialFilters.find((filter) => filter.field === 'projectName');
    activeFilters.current = projectFilter ? [projectFilter] : [];
    filtersActive.current = Boolean(projectFilter);
    const url = new URL(window.location.href);
    ['billingReportId', 'requestStatus', 'fromNotification']
      .forEach((key) => url.searchParams.delete(key));
    if (!projectFilter) {
      url.searchParams.delete('projectId');
      url.searchParams.delete('projectName');
    }
    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
  }, [initialFilters]);

  const transformPayload = useCallback((payload) => {
    const normalizedPayload = { ...payload };
    if (!filtersActive.current || !activeFilters.current.length) return normalizedPayload;

    const notificationFields = new Set(activeFilters.current.map((filter) => filter.field));
    return {
      ...normalizedPayload,
      filter: [
        ...(Array.isArray(normalizedPayload.filter) ? normalizedPayload.filter : [])
          .filter((filter) => !notificationFields.has(filter.field)),
        ...activeFilters.current,
      ],
    };
  }, []);

  return { handleApprovalUpdated, handleFilterUpdate, transformPayload };
};

const getNumericValue = (value) => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  const numericValue = Number(`${value ?? ''}`.replace(/,/g, ''));
  return Number.isFinite(numericValue) ? numericValue : 0;
};

export const getBillingReportPermissions = (permissions = {}) => {
  const hasAction = (moduleName, action) => {
    const candidates = [moduleName, moduleName.replace(/-/g, '_'), moduleName.replace(/_/g, '-')];
    return candidates.some((candidate) => (
      Array.isArray(permissions[candidate])
      && permissions[candidate].some((grantedAction) => (
        String(grantedAction).trim()
          .toLowerCase() === action
      ))
    ));
  };
  return {
    canCreate: hasAction(MODULE_NAMES.MIS_BILLING_REPORT, 'create'),
    canUpdate: hasAction(MODULE_NAMES.MIS_BILLING_REPORT, 'update'),
    canDelete: hasAction(MODULE_NAMES.MIS_BILLING_REPORT, 'delete'),
    canViewApprovalHistory: hasAction(MODULE_NAMES.MIS_BILLING_REPORT_APPROVAL_HISTORY, 'read'),
    canViewAmount: hasAction(MODULE_NAMES.MIS_BILLING_REPORT_AMOUNT_ADDITION, 'read'),
    canAddAmount: hasAction(MODULE_NAMES.MIS_BILLING_REPORT_AMOUNT_ADDITION, 'create'),
    canEditAmount: hasAction(MODULE_NAMES.MIS_BILLING_REPORT_AMOUNT_ADDITION, 'update'),
    canDeleteAmount: hasAction(MODULE_NAMES.MIS_BILLING_REPORT_AMOUNT_ADDITION, 'delete'),
  };
};

export const getFilterLabel = (filter) => {
  const values = Array.isArray(filter?.selectedValues)
    ? filter.selectedValues
    : filter?.selectedValue ? [filter.selectedValue] : [];
  return values
    .map((value) => (value && typeof value === 'object'
      ? value.label || value.value || ''
      : value))
    .filter(Boolean)
    .join(', ');
};

export const formatDateLabel = (value) => {
  if (!value) return '';
  const parsed = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(parsed.getTime())) return `${value}`;
  const day = `${parsed.getDate()}`.padStart(2, '0');
  const month = parsed.toLocaleString('en', { month: 'short' });
  return `${day}-${month}-${parsed.getFullYear()}`;
};

export const toApiDate = (value) => {
  if (!value) return '';
  const parsed = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  const month = `${parsed.getMonth() + 1}`.padStart(2, '0');
  const day = `${parsed.getDate()}`.padStart(2, '0');
  return `${parsed.getFullYear()}-${month}-${day}`;
};

export const isOneMonthBillingPeriod = (startValue, endValue) => {
  const startDate = toApiDate(startValue);
  const endDate = toApiDate(endValue);
  if (!startDate || !endDate) return false;
  const [year, month, day] = startDate.split('-').map(Number);
  const nextMonthLastDay = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  const expectedEndDate = day <= nextMonthLastDay
    ? new Date(Date.UTC(year, month, day - 1))
    : new Date(Date.UTC(year, month, nextMonthLastDay));
  return expectedEndDate.toISOString().slice(0, 10) === endDate;
};

export const formatIndianAmount = (value) => {
  if (value === null || value === undefined || value === '') return value;
  return new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 })
    .format(getNumericValue(value));
};

export const getSelectedProjectAndDate = (filters = [], tableConfiguration = []) => {
  const projectFilter = filters.find((filter) => filter.field === 'projectName');
  const dateFilter = filters.find((filter) => filter.field === 'date');
  const projectFilterDefinition = tableConfiguration
    .flatMap((column) => column.menuItems || [])
    .flatMap((menu) => menu.items || [])
    .find((item) => item.field === 'projectName');
  const selectedProjects = Array.isArray(projectFilter?.selectedValues)
    && projectFilter.selectedValues.length
    ? projectFilter.selectedValues.slice(0, 1)
    : projectFilter?.selectedValue ? [projectFilter.selectedValue] : [];
  const projectName = selectedProjects.map((selectedProject) => {
    const selectedValue = selectedProject && typeof selectedProject === 'object'
      ? selectedProject.value || selectedProject.id || selectedProject.label
      : selectedProject;
    const option = projectFilterDefinition?.options?.find((projectOption) => (
      `${projectOption.value}` === `${selectedValue}`
      || `${projectOption.label}` === `${selectedValue}`
    ));
    return option?.label
      || (selectedProject && typeof selectedProject === 'object'
        ? selectedProject.label || selectedProject.projectName || selectedProject.name
        : selectedProject);
  })
    .filter(Boolean)
    .join(', ');
  const selectedProject = selectedProjects[0];
  const selectedProjectValue = selectedProject && typeof selectedProject === 'object'
    ? selectedProject.value || selectedProject.id
    : selectedProject;
  const selectedProjectOption = projectFilterDefinition?.options?.find((option) => (
    `${option.value}` === `${selectedProjectValue}`
    || `${option.label}` === `${selectedProjectValue}`
  ));
  const range = Array.isArray(dateFilter?.range) ? dateFilter.range : [];
  return {
    projectName: projectName || getFilterLabel(projectFilter),
    projectId: selectedProjectOption?.value || selectedProjectValue || '',
    periodStartDate: toApiDate(range[0]),
    periodEndDate: toApiDate(range[1]),
    dateRange: range.length === 2
      ? `${formatDateLabel(range[0])} To ${formatDateLabel(range[1])}`
      : '',
  };
};

